import {
  Badge,
  Box,
  Flex,
  IconButton,
  Image,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import amatriumMarkDark from 'assets/image/amatrium_A_dark.png';
import amatriumMarkLight from 'assets/image/amatrium_A_light.png';
import amatriumDark from 'assets/image/amatrium_dark.png';
import amatriumLight from 'assets/image/amatrium_light.png';
import { ChevronDown, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { NavGroup } from './navConfig';
import UserMenu from './UserMenu';

/** localStorage key for per-group expand/collapse state (mirrors amatrium.sidebar.collapsed). */
const GROUPS_KEY = 'amatrium.sidebar.groups';

interface SidebarProps {
  groups: NavGroup[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** mobile drawer: close after navigating */
  onNavigate?: () => void;
  /** when true sidebar renders for a Drawer body (always expanded) */
  insideDrawer?: boolean;
  /** opens the "My Company" management modal (admin only) */
  onOpenCompany?: () => void;
}

// Amatrium brand logo image (matches mockup). mark=true → standalone "a" glyph for the
// collapsed rail; otherwise the full "amatrium" wordmark. Dark glyphs on light UI, white on dark.
const BrandLogo = ({ mark = false }: { mark?: boolean }) => {
  const wordmark = useColorModeValue(amatriumDark, amatriumLight);
  const glyph = useColorModeValue(amatriumMarkDark, amatriumMarkLight);
  return (
    <Image
      flexShrink={0}
      h={mark ? '30px' : '24px'}
      objectFit='contain'
      alt='Amatrium'
      src={mark ? glyph : wordmark}
    />
  );
};

const Sidebar = ({
  groups,
  collapsed,
  onToggleCollapse,
  onNavigate,
  insideDrawer = false,
  onOpenCompany,
}: SidebarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isCollapsed = collapsed && !insideDrawer;
  // Documents count for the nav pill (mockup "24"). Populated once the
  // Documents page has loaded; pill is omitted gracefully when 0.
  const documentCount = useSelector((state: any) => state.document?.total_item ?? 0);

  // Per-group expand/collapse. Primary "Tools" group open by default; others
  // collapsed to reduce first-load clutter. Persisted to localStorage.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(GROUPS_KEY);
      if (raw) return JSON.parse(raw) as Record<string, boolean>;
    } catch {
      /* ignore malformed storage */
    }
    return { 'shell.group.tools': true };
  });

  const toggleGroup = (labelKey: string) =>
    setOpenGroups((prev) => {
      const next = { ...prev, [labelKey]: !prev[labelKey] };
      try {
        localStorage.setItem(GROUPS_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });

  // Navigating into a collapsed group auto-expands it (merge-only; never auto-collapses others).
  useEffect(() => {
    const activeGroup = groups.find((g) => g.items.some((it) => it.path === location.pathname));
    if (activeGroup) {
      setOpenGroups((prev) =>
        prev[activeGroup.labelKey] ? prev : { ...prev, [activeGroup.labelKey]: true }
      );
    }
  }, [location.pathname, groups]);

  return (
    <Flex
      as='aside'
      direction='column'
      w='100%'
      h='100%'
      bg='bg.sidebar'
      borderColor='border.default'
      borderRightWidth={insideDrawer ? 0 : '1px'}
    >
      {/* Brand */}
      <Flex
        align='center'
        flexShrink={0}
        gap={3}
        h='topbar'
        px={4}
        borderColor='border.default'
        borderBottomWidth='1px'
      >
        <BrandLogo mark={isCollapsed} />
        {!isCollapsed && !insideDrawer && (
          <IconButton
            ml='auto'
            aria-label='Collapse sidebar'
            icon={<ChevronsLeft size={17} />}
            onClick={onToggleCollapse}
            size='ds-sm'
            title='Collapse sidebar'
            variant='ds-ghost'
          />
        )}
      </Flex>

      {/* Nav */}
      <Box flex='1' overflowY='auto' px={3} py={4}>
        {isCollapsed && (
          <Flex justify='center' mb={2}>
            <IconButton
              aria-label='Expand sidebar'
              icon={<ChevronsRight size={18} />}
              onClick={onToggleCollapse}
              size='ds-sm'
              variant='ds-ghost'
            />
          </Flex>
        )}

        {groups.map((group) => {
          // Rail mode always shows items (icon-only); otherwise gate by per-group state.
          const expanded = isCollapsed || !!openGroups[group.labelKey];
          return (
            <Box key={group.labelKey} mb={2}>
              {!isCollapsed && (
                <Flex
                  as='button'
                  align='center'
                  gap={2}
                  w='100%'
                  px={3}
                  pt={4}
                  pb={2}
                  color='text.subtle'
                  fontSize='xs'
                  fontWeight={600}
                  letterSpacing='0.06em'
                  textAlign='left'
                  textTransform='uppercase'
                  _hover={{ color: 'text.muted' }}
                  transition='color 0.14s ease-out'
                  aria-expanded={expanded}
                  onClick={() => toggleGroup(group.labelKey)}
                  type='button'
                >
                  <Box
                    as={ChevronDown}
                    flexShrink={0}
                    boxSize='13px'
                    opacity={0.65}
                    transform={expanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                    transition='transform 0.18s ease-out'
                  />
                  <Text as='span' noOfLines={1}>
                    {t(group.labelKey)}
                  </Text>
                </Flex>
              )}
              {expanded && (
                <Flex direction='column' gap={1}>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    const label = t(item.label);
                    const showCount = item.key === 'document' && documentCount > 0;
                    const link = (
                      <Flex
                        key={item.key}
                        as={item.action ? 'button' : Link}
                        pos='relative'
                        align='center'
                        justify={isCollapsed ? 'center' : 'flex-start'}
                        gap={3}
                        w={item.action && !isCollapsed ? '100%' : undefined}
                        px={isCollapsed ? 0 : 3}
                        py='10px'
                        color={isActive ? 'primary.solid' : 'text.muted'}
                        fontSize='base'
                        fontWeight={isActive ? 600 : 500}
                        textAlign='left'
                        bg={isActive ? 'primary.soft' : 'transparent'}
                        borderRadius='md'
                        _hover={{
                          bg: 'primary.soft',
                          color: isActive ? 'primary.solid' : 'text.default',
                        }}
                        whiteSpace='nowrap'
                        transition='background 0.14s ease-out, color 0.14s ease-out'
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => {
                          if (item.action) {
                            window.dispatchEvent(new CustomEvent(item.action));
                          }
                          onNavigate?.();
                        }}
                        {...(item.action ? {} : { to: item.path })}
                      >
                        <Box as={Icon} flexShrink={0} boxSize='19px' />
                        {!isCollapsed && (
                          <Text as='span' flex='1' noOfLines={1}>
                            {label}
                          </Text>
                        )}
                        {!isCollapsed && showCount && (
                          <Badge
                            flexShrink={0}
                            minW='22px'
                            px={1.5}
                            py='1px'
                            color={isActive ? 'primary.solid' : 'text.muted'}
                            fontFamily='mono'
                            fontSize='11px'
                            fontWeight={600}
                            textAlign='center'
                            textTransform='none'
                            bg={isActive ? 'primary.soft' : 'bg.canvas'}
                            borderWidth='1px'
                            borderColor='border.default'
                            borderRadius='full'
                          >
                            {documentCount > 999 ? '999+' : documentCount}
                          </Badge>
                        )}
                      </Flex>
                    );
                    return isCollapsed ? (
                      <Tooltip
                        key={item.key}
                        hasArrow
                        label={label}
                        openDelay={300}
                        placement='right'
                      >
                        {link}
                      </Tooltip>
                    ) : (
                      link
                    );
                  })}
                </Flex>
              )}
            </Box>
          );
        })}
      </Box>

      {/* User / account block — pinned to the bottom of the sidebar */}
      <Box
        flexShrink={0}
        mt='auto'
        px={isCollapsed ? 2 : 3}
        py={3}
        borderColor='border.default'
        borderTopWidth='1px'
      >
        <UserMenu collapsed={isCollapsed} onOpenCompany={onOpenCompany} onNavigate={onNavigate} />
      </Box>
    </Flex>
  );
};

export default Sidebar;
